import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useDocumentMeta({ title: "Admin Login", path: "/admin/login" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (res.ok) navigate("/admin");
    else setError(res.error);
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-charcoal flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-4 mb-8">
          <span className="h-px w-12 bg-gold" />
          <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-gold">
            AMADI Admin
          </span>
        </div>
        <h1 className="font-serif text-4xl text-canvas leading-tight">
          Insights CMS.
        </h1>
        <p className="mt-4 font-serif text-lg text-canvas/60">
          Sign in to manage published articles.
        </p>

        <form onSubmit={handleSubmit} data-testid="admin-login-form" className="mt-10 space-y-6">
          <div>
            <label className="block font-sans text-[11px] tracking-[0.2em] uppercase text-canvas/50 mb-3">
              Email
            </label>
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-transparent border border-canvas/25 px-4 py-4 font-serif text-base text-canvas placeholder:text-canvas/30 focus:outline-none focus:border-gold transition-colors duration-300"
              placeholder="admin@amadi-legal.com"
            />
          </div>
          <div>
            <label className="block font-sans text-[11px] tracking-[0.2em] uppercase text-canvas/50 mb-3">
              Password
            </label>
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-transparent border border-canvas/25 px-4 py-4 font-serif text-base text-canvas placeholder:text-canvas/30 focus:outline-none focus:border-gold transition-colors duration-300"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p data-testid="login-error" className="font-sans text-[13px] text-red-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-3 font-sans text-xs tracking-[0.14em] uppercase font-bold px-8 py-4 bg-gold text-charcoal border border-gold transition-colors duration-300 hover:bg-canvas hover:border-canvas disabled:opacity-60"
          >
            <Lock className="w-4 h-4" strokeWidth={1.6} />
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
